/**
 * Internal Webhook Contracts
 *
 * DATABASE-FIRST: Schemas match PostgreSQL exactly
 * Source: MCP postgres queries on 2026-01-02
 *
 * Used by:
 * - payment-service → backend (webhook forwarding)
 * - backend → depositsRepository/withdrawalsRepository (logging)
 */
import { z } from 'zod';
import { webhookStatusSchema, depositLogTypeSchema, withdrawalLogTypeSchema, } from '../common.js';
// =============================================================================
// WEBHOOK PROVIDER
// Source: payment_services.hash column values
// =============================================================================
/**
 * Known webhook provider identifiers (hashes)
 * These are the stable identifiers used in payment_services table
 */
export const webhookProviderValues = [
    'fservice', // FastPay
    'fservice_tjs', // FastPay TJS
    'nservice', // NirvanaPay
    'cservice', // Collybus
    'eservice', // EasySend
    'oservice', // 1Pays
    'fsl', // FastPay Long (payouts only)
];
export const webhookProviderSchema = z.enum(webhookProviderValues);
// =============================================================================
// DEPOSIT WEBHOOK (payment-service → backend)
// Endpoint: POST /api/internal/webhook/deposit
// =============================================================================
/**
 * Deposit webhook request
 * Sent by payment-service when provider confirms payment
 */
export const depositWebhookRequestSchema = z.object({
    provider: z.string(), // provider hash (e.g., 'fservice')
    dealId: z.string(), // provider's transaction ID (stored in deposits.provider_transaction_id)
    status: webhookStatusSchema,
    amount: z.number().optional(), // Optional - backend reads from DB if needed
    originalStatus: z.string(), // Raw status from provider (for logging)
    rawPayload: z.record(z.unknown()), // Full provider payload (for debugging)
});
/**
 * Deposit webhook response actions
 */
export const depositWebhookActionValues = [
    'marked_paid', // Payment confirmed, deposit marked as 'paid' (awaiting receipt)
    'credited', // Balance credited (legacy - not used in current flow)
    'already_processed', // Idempotent: already handled this webhook
    'not_found', // No deposit found for this dealId
    'status_unchanged', // Pending status, no action needed
    'failed', // Payment failed
    'cancelled', // Payment cancelled
];
export const depositWebhookActionSchema = z.enum(depositWebhookActionValues);
/**
 * Deposit webhook response
 */
export const depositWebhookResponseSchema = z.object({
    success: z.boolean(),
    transactionId: z.string().optional(), // deposits.reference_number
    action: depositWebhookActionSchema.optional(),
    error: z.string().optional(),
});
// =============================================================================
// PAYOUT WEBHOOK (payment-service → backend)
// Endpoint: POST /api/internal/webhook/payout
// =============================================================================
/**
 * Payout webhook request
 * Sent by payment-service when provider confirms payout
 */
export const payoutWebhookRequestSchema = z.object({
    provider: z.string(), // provider hash
    payoutId: z.string(), // provider's payout ID (stored in withdrawals.provider_transaction_id)
    status: webhookStatusSchema,
    amount: z.number().optional(),
    originalStatus: z.string(),
    rawPayload: z.record(z.unknown()),
});
/**
 * Payout webhook response actions
 */
export const payoutWebhookActionValues = [
    'completed', // Payout completed successfully
    'already_processed', // Idempotent
    'not_found', // No withdrawal found for this payoutId
    'status_unchanged', // Pending, no action
    'failed', // Payout failed
];
export const payoutWebhookActionSchema = z.enum(payoutWebhookActionValues);
/**
 * Payout webhook response
 */
export const payoutWebhookResponseSchema = z.object({
    success: z.boolean(),
    transactionId: z.string().optional(), // withdrawals.reference_number
    payoutLogId: z.string().optional(), // Legacy field
    action: payoutWebhookActionSchema.optional(),
    error: z.string().optional(),
});
// =============================================================================
// GENERIC WEBHOOK PAYLOAD
// Used for routing webhooks by type
// =============================================================================
/**
 * Unified webhook payload (used in payment-service)
 */
export const webhookPayloadSchema = z.object({
    provider: z.string(),
    type: z.enum(['deposit', 'payout']),
    dealId: z.string(),
    status: webhookStatusSchema,
    originalStatus: z.string(),
    amount: z.number().optional(),
    rawPayload: z.record(z.unknown()),
});
// =============================================================================
// LOG ENTITY SCHEMAS
// NOTE: Log event input/result schemas are in wallet/deposit.ts and wallet/withdrawal.ts
// (use snake_case for direct DB stored procedure calls)
// Match deposit_logs and withdrawal_logs table structures
// =============================================================================
/**
 * Deposit log entity
 * Matches: deposit_logs table columns exactly
 */
export const depositLogEntitySchema = z.object({
    id: z.number(),
    depositRef: z.string(),
    logType: depositLogTypeSchema,
    cascadeStats: z.record(z.unknown()).nullable(),
    apiResponse: z.record(z.unknown()).nullable(),
    providerHash: z.string().nullable(),
    providerTransactionId: z.string().nullable(),
    photoUrl: z.string().nullable(),
    staffId: z.string().nullable(),
    note: z.string().nullable(),
    previousValues: z.record(z.unknown()).nullable(),
    newValues: z.record(z.unknown()).nullable(),
    result: z.string().nullable(),
    createdAt: z.string().datetime(),
});
/**
 * Withdrawal log entity
 * Matches: withdrawal_logs table columns exactly
 */
export const withdrawalLogEntitySchema = z.object({
    id: z.number(),
    withdrawalRef: z.string(),
    logType: withdrawalLogTypeSchema,
    cascadeStats: z.record(z.unknown()).nullable(),
    apiResponse: z.record(z.unknown()).nullable(),
    providerHash: z.string().nullable(),
    providerTransactionId: z.string().nullable(),
    photoUrl: z.string().nullable(),
    staffId: z.string().nullable(),
    note: z.string().nullable(),
    previousValues: z.record(z.unknown()).nullable(),
    newValues: z.record(z.unknown()).nullable(),
    result: z.string().nullable(),
    createdAt: z.string().datetime(),
});
//# sourceMappingURL=webhook.js.map